/**
 * Homepage personalization that stays cache-safe.
 *
 * The homepage's HTML is one shared, CDN-cached artifact
 * (ApplicationController::CACHEABLE_PUBLIC_ROUTES) — nothing here may depend
 * on who is asking at render time. Everything per-visitor happens in the
 * browser, and everything network-dependent happens off the critical path:
 *
 *   - reprise CTA relabel for returning quiz-takers (localStorage)
 *   - geo-aware city tiles + hero city preselect (/geo.json edge Worker,
 *     falling back to the timezone, falling back to doing nothing)
 *   - company proof under the hero email field (POST on typing, never on load)
 *   - "Your favorites" strip for signed-in visitors (authenticated fetch;
 *     the csrf-token meta is the signed-in signal — it is suppressed on
 *     cached anonymous renders, same convention as discover.js)
 *   - mobile sticky quiz CTA (IntersectionObserver)
 *
 * The pre-paint pieces (resume ribbon, recently-viewed strip) are NOT here —
 * they must run synchronously during parse and live inline in
 * _hero_quiz.html.erb and _personal_strips.html.erb.
 */
import { initializeCarousels } from '~/javascripts/carousel.js';
import initCompanyProof from '~/javascripts/activity-quiz-company-proof.js';

function track(event, extra) {
  if (window.gtag) window.gtag('event', event, extra || {});
}

function savedQuiz() {
  try {
    const data = JSON.parse(localStorage.getItem('tbRecoQuiz'));
    if (data && data.answers && data.savedAt &&
        Date.now() - data.savedAt < 90 * 24 * 60 * 60 * 1000) return data;
  } catch (e) { /* blocked or corrupt: treat as first visit */ }
  return null;
}

// --- Reprise card: speak to the returning quiz-taker -------------------------

function initRepriseSwap() {
  if (!savedQuiz()) return;
  const cta = document.querySelector('[data-reprise-cta]');
  if (cta) cta.textContent = 'See your matches';
}

// --- Geo: float the visitor's city tile forward, preselect the quiz city -----

// City names (lowercased) as an edge worker or browser reports them, mapped to
// the Location vanity_slugs the homepage's twelve tiles carry.
const GEO_CITY_SLUGS = {
  'new york': 'new-york-city', 'new york city': 'new-york-city',
  'brooklyn': 'new-york-city', 'queens': 'new-york-city',
  'los angeles': 'los-angeles', 'san francisco': 'san-francisco',
  'oakland': 'san-francisco', 'san jose': 'san-francisco',
  'chicago': 'chicago', 'boston': 'boston',
  'washington': 'washington-dc', 'arlington': 'washington-dc',
  'seattle': 'seattle', 'austin': 'austin', 'denver': 'denver',
  'dallas': 'dallas', 'fort worth': 'dallas', 'philadelphia': 'philadelphia'
};

// US state/region → the nearest of the twelve. Deliberately partial: a state
// with no defensible nearest market (the Southeast) maps to nothing, and the
// page keeps its curated order rather than guessing.
const GEO_STATE_SLUGS = {
  NY: 'new-york-city', NJ: 'new-york-city', CT: 'new-york-city',
  CA: 'los-angeles', NV: 'los-angeles',
  IL: 'chicago', WI: 'chicago', IN: 'chicago', MI: 'chicago',
  MN: 'chicago', IA: 'chicago', MO: 'chicago', OH: 'chicago',
  MA: 'boston', NH: 'boston', VT: 'boston', ME: 'boston', RI: 'boston',
  DC: 'washington-dc', MD: 'washington-dc', VA: 'washington-dc',
  WA: 'seattle', OR: 'seattle', ID: 'seattle', AK: 'seattle',
  TX: 'dallas', OK: 'dallas', AR: 'dallas', LA: 'dallas',
  CO: 'denver', UT: 'denver', WY: 'denver', MT: 'denver',
  NM: 'denver', AZ: 'denver', KS: 'denver', NE: 'denver',
  SD: 'denver', ND: 'denver',
  PA: 'philadelphia', DE: 'philadelphia'
};

const TIMEZONE_SLUGS = {
  'America/New_York': 'new-york-city',
  'America/Chicago': 'chicago',
  'America/Denver': 'denver',
  'America/Phoenix': 'denver',
  'America/Los_Angeles': 'los-angeles'
};

function slugFromGeo(geo) {
  if (!geo || (geo.country && geo.country !== 'US')) return null;
  const city = (geo.city || '').toLowerCase();
  return GEO_CITY_SLUGS[city] || GEO_STATE_SLUGS[(geo.region || '').toUpperCase()] || null;
}

function slugFromTimezone() {
  try {
    return TIMEZONE_SLUGS[Intl.DateTimeFormat().resolvedOptions().timeZone] || null;
  } catch (e) {
    return null;
  }
}

function applyCitySlug(slug) {
  if (!slug) return;

  // Float the tile to position 2, right after Virtual. Same-size tiles in a
  // scroll rail / grid, so reordering shifts nothing vertically.
  const rail = document.querySelector('.ds-carousel--city-grid [data-carousel-track]');
  const tile = rail && rail.querySelector(`[data-city-slug="${slug}"]`);
  if (tile && rail.children[1] && rail.children[1] !== tile) {
    rail.insertBefore(tile, rail.children[1]);
  }

  // Preselect the hero quiz's city — visible, editable, and NOT marked as
  // answered: the server only records what the visitor actually stated, and a
  // guess from geolocation is not that.
  const citySelect = document.getElementById('hq-city');
  if (citySelect && !citySelect.disabled && !citySelect.value &&
      citySelect.querySelector(`option[value="${slug}"]`)) {
    citySelect.value = slug;
  }

  track('personalization_shown', { feature: 'geo_city', city_slug: slug });
}

function initGeo() {
  const run = () => {
    // /geo.json is answered at the CDN edge (see cloudflare/geo-worker.js) and
    // never reaches the origin. Where the worker isn't deployed (development)
    // this 404s inside 1.5s and the timezone heuristic takes over.
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 1500);
    fetch('/geo.json', { signal: abort.signal, credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((geo) => {
        clearTimeout(timeout);
        applyCitySlug(geo ? slugFromGeo(geo) : slugFromTimezone());
      });
  };
  if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}

// --- Company proof under the hero email field --------------------------------

// --- "Your favorites" strip for signed-in visitors -----------------------------

function initShortlistStrip() {
  // csrf-token meta is suppressed on cached anonymous renders, so its presence
  // is the page telling us this render was fresh and signed-in.
  if (!document.querySelector('meta[name="csrf-token"]')) return;

  const slot = document.querySelector('[data-strip="shortlist"]');
  if (!slot || slot.dataset.loaded) return;
  slot.dataset.loaded = '1';

  fetch('/favorites/strip', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin'
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (!data || !data.html) return;
      slot.innerHTML = data.html;
      slot.hidden = false;
      // Ease the late-arriving strip in rather than popping it into the page.
      slot.classList.add('ds-anim-fade-up');
      const section = slot.closest('[data-personal-strips]');
      if (section) section.hidden = false;
      initializeCarousels(slot);
      track('personalization_shown', { feature: 'shortlist_strip' });
    })
    .catch(() => {});
}

// --- "Your saved quotes" strip --------------------------------------------------

function initSavedQuotesStrip() {
  // Saved quotes live in the Rails session, which JS can't read; save_quote
  // sets the tb_has_saved_quotes hint cookie so cold visitors (the vast
  // majority on a cached homepage) skip the fetch entirely.
  if (!/(?:^|;\s*)tb_has_saved_quotes=/.test(document.cookie)) return;

  const slot = document.querySelector('[data-strip="quotes"]');
  if (!slot || slot.dataset.loaded) return;
  slot.dataset.loaded = '1';

  fetch('/bookings/saved_quotes_strip', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin'
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (!data || !data.html) return;
      slot.innerHTML = data.html;
      slot.hidden = false;
      slot.classList.add('ds-anim-fade-up');
      const section = slot.closest('[data-personal-strips]');
      if (section) section.hidden = false;
      initializeCarousels(slot);
      track('personalization_shown', { feature: 'saved_quotes_strip' });
    })
    .catch(() => {});
}

// --- Mobile sticky quiz CTA ---------------------------------------------------

function initStickyCta() {
  const cta = document.querySelector('[data-sticky-quiz-cta]');
  const hero = document.getElementById('find-my-match');
  if (!cta || !hero || cta.dataset.bound) return;
  cta.dataset.bound = '1';

  // Hide while anything that already asks is on screen: the hero quiz, either
  // CTA card (reprise + advisor banner), or the footer.
  const blockers = [hero].concat(
    Array.from(document.querySelectorAll('.ds-cta-card, footer.ds-footer'))
  );
  const visible = new Set();
  let passedHero = false;
  let hideTimer = null;

  function update() {
    const show = passedHero && visible.size === 0;
    if (show) {
      clearTimeout(hideTimer);
      cta.hidden = false;
      requestAnimationFrame(() => cta.classList.add('is-shown'));
    } else if (cta.classList.contains('is-shown')) {
      cta.classList.remove('is-shown');
      hideTimer = setTimeout(() => { cta.hidden = true; }, 300);
    }
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) visible.add(entry.target);
      else visible.delete(entry.target);
      if (entry.target === hero) {
        passedHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      }
    });
    update();
  });
  blockers.forEach((el) => io.observe(el));

  cta.addEventListener('click', () => {
    track('cta_clicked', { cta_id: 'sticky_quiz_cta' });
  });
}

// --- Boot ---------------------------------------------------------------------

function boot() {
  if (!document.getElementById('reco-hero-quiz')) return;

  // Company proof follows the existing homepage email field behavior.
  initCompanyProof();
  if (!document.querySelector('[data-personal-strips]')) return;

  initRepriseSwap();
  initGeo();
  initShortlistStrip();
  initSavedQuotesStrip();
  initStickyCta();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
document.addEventListener('turbolinks:load', boot);
document.addEventListener('turbo:load', boot);
